import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store';
import type { AppDispatch } from '../store';
import { type TenancyTO } from '../lib';
import {
  getInsightSettings,
  getTimeline,
  updateInsightSettings,
} from '../store/ai2';
import type { InsightSettingsData } from '../lib/responses/getInsightSettings';
import type { UpdateInsightSettingsPayload } from '../lib/payloads/updateInsightSettings';
import moment from 'moment';
import { usePatientProfile } from '../ai2/components/ProfileBar/hooks';

interface UpdatePayloadItem {
  mpp_id: string | number;
  settings: Record<string, unknown>;
}

interface PatientInsightSettingsContextValue {
  insightSettings?: InsightSettingsData;
  isLoading: boolean;
  message?: string | null;
  selectedTenancy?: TenancyTO | null;
  fetchInsightSettings: (
    accessToken: string | null,
    tenancyId: string | number,
  ) => Promise<void>;
  updateSettings: (
    accessToken: string | null,
    tenancyId: string | number,
    payload: UpdatePayloadItem[],
  ) => Promise<void>;
  applyInsightSettings: (
    payload: Partial<UpdateInsightSettingsPayload>,
  ) => Promise<void>;
  refreshTimeline: (
    accessToken: string | null,
    tenancyId: string | number,
    fromISO?: string,
    toISO?: string,
    force?: boolean,
  ) => Promise<void>;
}

const PatientInsightSettingsContext = createContext<
  PatientInsightSettingsContextValue | undefined
>(undefined);

export const PatientInsightSettingsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { insightSettings, isLoading, message } = useSelector(
    (state: RootState) => state.insightSettings,
  );
  const { patientProfile } = usePatientProfile();
  const accessToken = patientProfile.access_token;
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );

  const fetchInsightSettings = useCallback(
    async (accessToken: string | null, tenancyId: string | number) => {
      if (!accessToken || !tenancyId) return;
      await dispatch(getInsightSettings(accessToken, tenancyId));
    },
    [dispatch],
  );

  const lastFetchRef = useRef<{
    token: string | null;
    tenancyId: string | number | null;
  }>({
    token: null,
    tenancyId: null,
  });
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    const token = accessToken ?? null;
    const tenancyId = selectedTenancy?.tenancy_id ?? null;
    if (!token || !tenancyId) return;

    const unchanged =
      hasFetchedRef.current &&
      lastFetchRef.current.token === token &&
      lastFetchRef.current.tenancyId === tenancyId;
    if (unchanged) return;

    lastFetchRef.current = { token, tenancyId };
    hasFetchedRef.current = true;
    fetchInsightSettings(token, tenancyId);
  }, [accessToken, selectedTenancy?.tenancy_id, fetchInsightSettings]);

  const value = useMemo<PatientInsightSettingsContextValue>(
    () => ({
      insightSettings,
      isLoading,
      message,
      selectedTenancy,
      fetchInsightSettings,
      updateSettings: async (accessToken, tenancyId, payload) => {
        if (!accessToken || !tenancyId) return;
        await dispatch(updateInsightSettings(tenancyId, accessToken, payload));
        await dispatch(getInsightSettings(accessToken, tenancyId));
      },
      applyInsightSettings: async (payload) => {
        const token =
          (payload?.access_token as string | undefined) ?? accessToken ?? null;
        const tenancyId =
          (payload?.tenancyId as number | undefined) ??
          selectedTenancy?.tenancy_id ??
          null;
        if (!token || !tenancyId) return;
        const merged: UpdateInsightSettingsPayload = {
          access_token: token,
          tenancyId,
          medication_settings: payload?.medication_settings,
          appointment_settings: payload?.appointment_settings,
          settings: payload?.settings,
        };
        await dispatch(updateInsightSettings(merged));
        await dispatch(getInsightSettings(token, tenancyId, true));
      },
      refreshTimeline: async (
        accessToken,
        tenancyId,
        fromISO = moment().add(-2, 'year').format(),
        toISO = moment().add(0, 'year').format(),
        force = true,
      ) => {
        if (!accessToken || !tenancyId) return;
        await dispatch(
          getTimeline(accessToken, fromISO, toISO, tenancyId, force),
        );
      },
    }),
    [
      dispatch,
      insightSettings,
      isLoading,
      message,
      selectedTenancy,
      accessToken,
    ],
  );

  return (
    <PatientInsightSettingsContext.Provider value={value}>
      {children}
    </PatientInsightSettingsContext.Provider>
  );
};

export const usePatientInsightSettings =
  (): PatientInsightSettingsContextValue => {
    const ctx = useContext(PatientInsightSettingsContext);
    if (!ctx) {
      throw new Error(
        'usePatientInsightSettings must be used within PatientInsightSettingsProvider',
      );
    }
    return ctx;
  };
